import type { ApolloError, FetchPolicy } from '@apollo/client';
import { endOfDay, startOfDay } from 'date-fns';
import { useTypedLazyQuery, useTypedQuery } from '../lib/generated/zeus/apollo';
import { genericScalars } from '../lib/graphql/helpers';
import { objValueNullToUndefined } from '../lib/utils';
import { teamRosterSelector } from './selectors/team-roster';
import type { DerivedTeamRoster } from './types';

export const queryTeamRosterName = 'queryTeamRoster';

const lazyQueryTeamRosterName = 'queryTeamRosterLazy';

type QueryReturn = {
  readonly queryTeamRoster?: readonly DerivedTeamRoster[];
};

const noCache: FetchPolicy = 'no-cache';

const typedQuery = (
  from: Date,
  to: Date,
  teamId?: string,
  userId?: string
) => ({
  fromDate: startOfDay(from),
  toDate: endOfDay(to),
  teamId,
  userId,
});

const typedQueryOptions = ({
  operationName,
  cache,
  onError,
  onCompleted,
}: {
  readonly operationName: string;
  readonly cache: boolean;
  readonly onError: (e: ApolloError) => void;
  readonly onCompleted?: (data: readonly DerivedTeamRoster[]) => void;
}) => ({
  apolloOptions: {
    defaultOptions: cache ? {} : { fetchPolicy: noCache },
    onCompleted:
      onCompleted === undefined
        ? undefined
        : (data: QueryReturn) =>
            onCompleted(
              (data.queryTeamRoster ?? [])
                .map(objValueNullToUndefined)
                .filter(
                  (roster): roster is DerivedTeamRoster => roster !== undefined
                )
            ),
    onError,
  },
  scalars: genericScalars,
  operationOptions: { operationName },
});

/**
 * Query team roster by providing team id and date range.
 * This LazyQuery will not cache the result.
 *
 * @param from - start date of the date range
 * @param to - end date of the date range
 * @param teamId - the team id to query
 * @param userId - the user id to query
 * @param onCompleted - callback function to be called when the query is completed
 * @returns set of team roster that matched the query.
 */
export const useLazyQueryTeamRoster = (
  from: Date,
  to: Date,
  onError: (e: ApolloError) => void,
  teamId?: string,
  userId?: string,
  onCompleted?: (d: readonly DerivedTeamRoster[]) => void
) =>
  useTypedLazyQuery(
    {
      queryTeamRoster: [
        typedQuery(from, to, teamId, userId),
        teamRosterSelector,
      ],
    },
    typedQueryOptions({
      operationName: lazyQueryTeamRosterName,
      cache: false,
      onCompleted,
      onError,
    })
  );

/**
 * Query team roster by providing team id and date range.
 *
 * @param from - start date of the date range
 * @param to - end date of the date range
 * @param teamId - the team id to query
 * @param userId - the user id to query
 * @param onCompleted - callback function to be called when the query is completed
 * @returns set of team roster that matched the query.
 */
const useQueryTeamRoster = (
  from: Date,
  to: Date,
  onError: (e: ApolloError) => void,
  teamId?: string,
  userId?: string,
  onCompleted?: (d: readonly DerivedTeamRoster[]) => void
) =>
  useTypedQuery(
    {
      queryTeamRoster: [
        typedQuery(from, to, teamId, userId),
        teamRosterSelector,
      ],
    },
    typedQueryOptions({
      operationName: queryTeamRosterName,
      cache: false,
      onError,
      onCompleted,
    })
  );

export default useQueryTeamRoster;
